import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import queryString from 'query-string';
import {
  Chat, Channel, Thread, Window,
  MessageList, MessageInput,
} from 'stream-chat-react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText,
} from '@material-ui/core';

import chatClientInstance from '../../common/services/chatClient';
import chatTranslateInstance from '../../common/services/chatTranslate';
import { doFileUploadRequest } from '../../common/services/validateFile';
import { fetchClasstalkUser } from '../../common/services/worktimeService';
import CustomAttachment from '../components/CustomAttachment';
import { AppBar } from '../components';
import { Spinner } from '../../common/components';
import {
  MESSAGE_ACTIONS_CLASSTALK,
} from '../../common/constants';
import { isOutOfWorktime, nl2br } from '../../common/utils';

import 'stream-chat-react/dist/css/v2/index.css';
import '../chats.css';

const S = {
  Wrapper: styled.div`
    display: flex;
    width: 100%;
  `,
  AppBar: styled(AppBar)`
    position: fixed;
    width: 100%;
    box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.03), 0 7px 9px 0 rgba(0, 0, 0, 0.03);
    background-color: #fff;
    z-index: 50;
  `,
  ButtonLeaveMessage: styled(Button)`
    color: rgba(0, 0, 0, 0.54);
    background-color: #fff;
  `,
  ButtonConfirm: styled(Button)`
    color: #fff;
    background-color: #00c896;
    &:hover {
      background-color: #00b386;
    }
  `,
};

class DirectChats extends Component {
  state = {
    chatClient: null,
    channel: null,
    modalOpen: false,
    outOfWorktime: false,
  };

  async componentDidMount() {
    const { match } = this.props;

    const { channelID } = queryString.parse(match.params.params);
    const chatClient = chatClientInstance.getChatClient();
    const channel = await chatClient.channel('classtalk', channelID);
    await channel.watch();

    this.setState({
      chatClient,
      channel,
    });

    await this.checkWorktime(channel, chatClient);
  }

  checkWorktime = async (channel, chatClient) => {
    const clazzData = channel.data?.clazz;
    if (!clazzData) return;

    const currentUserId = chatClient.userID;
    const teacher = clazzData.teacher || clazzData.admin;
    if (!teacher || teacher.id === currentUserId) return;

    const teacherId = teacher.id;

    try {
      const response = await fetchClasstalkUser(teacherId);
      const classtalkUser = response.data;
      const outOfWork = isOutOfWorktime(classtalkUser);

      this.setState({
        outOfWorktime: outOfWork,
        modalOpen: outOfWork,
      });
    } catch (error) {
      // fallback: worktime 조회 실패 시 제한 없이 허용
    }
  }

  handleAlertClose = () => {
    this.setState({ modalOpen: false });
  }

  handleAlertConfirm = () => {
    const { history } = this.props;
    this.setState({ modalOpen: false });
    history.push({ pathname: '/' });
  }

  handleMessageSubmit = (message) => {
    const { channel, outOfWorktime } = this.state;
    channel.sendMessage(
      { ...message, silent: outOfWorktime },
      { skip_push: outOfWorktime },
    );
  }

  render() {
    const {
      chatClient,
      channel,
      modalOpen,
    } = this.state;
    const { location } = this.props;
    const i18n = chatTranslateInstance.getChatTranslate();
    const i18nInstance = i18n.geti18Instance();

    const searchParams = new URLSearchParams(location.search);
    const platform = searchParams.get('platform');
    const isMobile = platform === 'mobile';

    if (!channel) return (<Spinner />);
    channel.markRead();

    return (
      <S.Wrapper>
        <S.AppBar />
        <Chat client={chatClient} theme="messaging light" i18nInstance={i18n}>
          <Channel
            channel={channel}
            Attachment={CustomAttachment}
            imageAttachmentSizeHandler={(attachment) => ({
              url: attachment.image_url || attachment.thumb_url,
            })}
          >
            <Window>
              <MessageList
                messageActions={MESSAGE_ACTIONS_CLASSTALK}
                unsafeHTML
              />
              <MessageInput
                disableMentions
                doFileUploadRequest={doFileUploadRequest}
                doImageUploadRequest={doFileUploadRequest}
                overrideSubmitHandler={this.handleMessageSubmit}
                maxRows={4}
                grow
                shouldSubmit={isMobile ? () => false : undefined}
              />
            </Window>
            <Thread />
          </Channel>
        </Chat>
        <Dialog
          open={modalOpen}
          onClose={this.handleAlertClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent style={{ marginTop: '20px' }}>
            <DialogContentText id="alert-dialog-description">
              { nl2br(i18nInstance.t('MESSAGE_OUT_OF_OFFICE')) }
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <S.ButtonLeaveMessage onClick={this.handleAlertClose}>
              { i18nInstance.t('MESSAGE_LEAVE_MESSAGE') }
            </S.ButtonLeaveMessage>
            <S.ButtonConfirm onClick={this.handleAlertConfirm}>
              { i18nInstance.t('MESSAGE_CONFIRM') }
            </S.ButtonConfirm>
          </DialogActions>
        </Dialog>
      </S.Wrapper>
    );
  }
}

DirectChats.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      params: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default DirectChats;
